import { useEffect } from 'react';

type SeoProps = {
  title: string;
  description: string;
  canonicalPath: string;
  robots?: string;
  about?: 'organization' | 'learningService';
  mainEntity?: Record<string, unknown>;
  breadcrumb?: Record<string, unknown>;
  schema?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_URL = 'https://learnwebacademy.net';
const DEFAULT_IMAGE = `${SITE_URL}/learnwebacademy-og.webp`;
const SCHEMA_SCRIPT_ID = 'structured-data';
const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const LEARNING_SERVICE_ID = `${SITE_URL}/#learning-service`;

const setMetaTag = (selector: string, attribute: 'content' | 'href', value: string) => {
  const element = document.head.querySelector(selector);

  if (element) {
    element.setAttribute(attribute, value);
  }
};

const ensureCanonicalLink = () => {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }

  return canonical;
};

const updateSchemaMarkup = (schema: Record<string, unknown> | Record<string, unknown>[]) => {
  let script = document.head.querySelector<HTMLScriptElement>(`#${SCHEMA_SCRIPT_ID}`);

  if (!script) {
    script = document.createElement('script');
    script.id = SCHEMA_SCRIPT_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
};

const getOrganizationSchema = () => ({
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: 'LearnWebAcademy',
  legalName: 'OLBMI ltd.',
  url: `${SITE_URL}/`,
  logo: {
    '@type': 'ImageObject',
    '@id': `${SITE_URL}/#logo`,
    url: DEFAULT_IMAGE,
    contentUrl: DEFAULT_IMAGE,
  },
  email: 'support@learnwebacademy.net',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '10 Mnasiadou, 2nd Floor',
    postalCode: '1065',
    addressLocality: 'Nicosia',
    addressCountry: 'Cyprus',
  },
  identifier: {
    '@type': 'PropertyValue',
    propertyID: 'Company Number',
    value: 'HE489301',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'support@learnwebacademy.net',
  },
});

const getWebsiteSchema = () => ({
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: `${SITE_URL}/`,
  name: 'LearnWebAcademy',
  publisher: getOrganizationSchema(),
});

const getLearningServiceSchema = () => ({
  '@type': 'Service',
  '@id': LEARNING_SERVICE_ID,
  name: 'LearnWebAcademy Digital Learning Service',
  serviceType: 'Online web development and digital skills learning service',
  description:
    'An online learning service providing web development courses, digital skills training, learning materials, and access to a digital course library.',
  url: `${SITE_URL}/`,
  provider: getOrganizationSchema(),
});

const getWebPageId = (canonicalPath: string) => {
  const pathWithoutQuery = canonicalPath.split('?')[0];

  if (pathWithoutQuery === '/') {
    return `${SITE_URL}/#webpage`;
  }

  return `${SITE_URL}${pathWithoutQuery.replace(/\/$/, '')}/#webpage`;
};

const getDefaultSchema = (
  title: string,
  description: string,
  canonicalPath: string,
  url: string,
  about: SeoProps['about'] = 'learningService',
  mainEntity?: Record<string, unknown>,
  breadcrumb?: Record<string, unknown>,
) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': getWebPageId(canonicalPath),
  url,
  name: title,
  description,
  isPartOf: getWebsiteSchema(),
  about: about === 'organization' ? getOrganizationSchema() : getLearningServiceSchema(),
  ...(mainEntity ? { mainEntity } : {}),
  ...(breadcrumb ? { breadcrumb } : {}),
  publisher: getOrganizationSchema(),
});

const Seo = ({
  title,
  description,
  canonicalPath,
  robots = 'index, follow',
  about,
  mainEntity,
  breadcrumb,
  schema,
}: SeoProps) => {
  useEffect(() => {
    const url = `${SITE_URL}${canonicalPath}`;
    const schemaMarkup =
      schema || getDefaultSchema(title, description, canonicalPath, url, about, mainEntity, breadcrumb);

    document.title = title;

    setMetaTag('meta[name="robots"]', 'content', robots);
    setMetaTag('meta[name="description"]', 'content', description);
    setMetaTag('meta[property="og:title"]', 'content', title);
    setMetaTag('meta[property="og:description"]', 'content', description);
    setMetaTag('meta[property="og:url"]', 'content', url);
    setMetaTag('meta[property="og:image"]', 'content', DEFAULT_IMAGE);
    ensureCanonicalLink().href = url;
    updateSchemaMarkup(schemaMarkup);
  }, [about, breadcrumb, canonicalPath, description, mainEntity, robots, schema, title]);

  return null;
};

export default Seo;
